'use es6';

import { createSelector } from 'reselect';
import { ContentTypes, RequestStatus } from '../constants/base';
import { getMatchContentSlugType } from './matchSelectors';
import { getHasFreeBlogContent, getHasFreeLandingPages, getHasFreeSitePages } from './authSelectors';
import { getBlogQuantityUsed, getBlogUsageLimit } from './blogUsageSelectors';
const MIN_THRESHOLD_TO_DISPLAY_FREE_SITE_PAGES_BANNER = 5;
const MIN_THRESHOLD_TO_DISPLAY_STARTER_SITE_PAGES_BANNER = 10;
const MIN_THRESHOLD_TO_DISPLAY_FREE_LANDING_PAGES_BANNER = 5;
const MIN_THRESHOLD_TO_DISPLAY_FREE_BLOG_POSTS_BANNER = 10;

const getContentUsageLimitsState = state => state.contentUsageLimits;

export const getUsageLimit = createSelector(getContentUsageLimitsState, contentUsage => contentUsage.limit);
export const getContentQuantityUsed = createSelector(getContentUsageLimitsState, contentUsage => contentUsage.quantityUsed);
export const getContentUsageStatus = createSelector(getContentUsageLimitsState, contentUsage => contentUsage.status);
export const getHasReachedContentLimit = createSelector([getUsageLimit, getContentQuantityUsed], (limit, used) => limit !== null && used >= limit);
export const getIsOverContentLimit = createSelector([getUsageLimit, getContentQuantityUsed], (limit, used) => limit !== null && used > limit);
export const getAmountMoreThanLimitDifference = createSelector([getUsageLimit, getContentQuantityUsed], (limit, used) => limit !== null && used > limit ? used - limit : 0);
export const getShouldShowStarterSitePageUpgradeBanner = createSelector([getUsageLimit, getContentUsageStatus, getContentQuantityUsed, getHasFreeSitePages], (contentLimit, contentUsageStatus, contentQuantityUsed, hasFreeSitePages) => {
  if (hasFreeSitePages || contentUsageStatus !== RequestStatus.SUCCEEDED || !contentLimit) {
    return false;
  }

  const numPagesBelowLimit = contentLimit - contentQuantityUsed;
  return numPagesBelowLimit <= MIN_THRESHOLD_TO_DISPLAY_STARTER_SITE_PAGES_BANNER && numPagesBelowLimit >= 0;
});
export const getShouldShowFreeSitePageUpgradeBanner = createSelector([getUsageLimit, getContentUsageStatus, getContentQuantityUsed, getHasFreeSitePages], (contentLimit, contentUsageStatus, contentQuantityUsed, hasFreeSitePages) => {
  if (contentUsageStatus !== RequestStatus.SUCCEEDED || !hasFreeSitePages || !contentLimit) {
    return false;
  }

  const numPagesBelowLimit = contentLimit - contentQuantityUsed;
  return numPagesBelowLimit <= MIN_THRESHOLD_TO_DISPLAY_FREE_SITE_PAGES_BANNER && numPagesBelowLimit >= 0;
});
export const getShouldShowFreeLandingPageUpgradeBanner = createSelector([getUsageLimit, getContentUsageStatus, getContentQuantityUsed, getHasFreeLandingPages], (contentLimit, contentUsageStatus, contentQuantityUsed, hasFreeLandingPages) => {
  if (contentUsageStatus !== RequestStatus.SUCCEEDED || !hasFreeLandingPages || !contentLimit) {
    return false;
  }

  const numPagesBelowLimit = contentLimit - contentQuantityUsed;
  return numPagesBelowLimit <= MIN_THRESHOLD_TO_DISPLAY_FREE_LANDING_PAGES_BANNER && numPagesBelowLimit >= 0;
});
export const getShouldShowFreeBlogPostUpgradeBanner = createSelector([getUsageLimit, getContentUsageStatus, getContentQuantityUsed, getHasFreeBlogContent], (contentLimit, contentUsageStatus, contentQuantityUsed, hasFreeBlogContent) => {
  if (contentUsageStatus !== RequestStatus.SUCCEEDED || !hasFreeBlogContent || !contentLimit) {
    return false;
  }

  const numPostsBelowLimit = contentLimit - contentQuantityUsed;
  return numPostsBelowLimit <= MIN_THRESHOLD_TO_DISPLAY_FREE_BLOG_POSTS_BANNER && numPostsBelowLimit >= 0;
});
export const getShouldUseCmsStarterSitePagesUpgradePoint = createSelector([getContentUsageStatus, getContentQuantityUsed, getUsageLimit, getMatchContentSlugType], (usageStatus, contentQuantityUsed, contentLimit, contentType) => usageStatus === RequestStatus.SUCCEEDED && contentType === ContentTypes.SITE_PAGE && contentLimit !== null && contentQuantityUsed < 40);
export const getContentUsageLimitsDataForDowngradeAlerts = createSelector([getMatchContentSlugType, getContentQuantityUsed, getUsageLimit, getBlogQuantityUsed, getBlogUsageLimit], (contentType, contentQuantityUsed, contentUsageLimit, blogQuantityUsed, blogUsageLimit) => {
  const limitsData = {};
  const contentLimitKey = contentType === ContentTypes.SITE_PAGE ? 'SITE_PAGE_LIMITS' : contentType === ContentTypes.LANDING_PAGE ? 'LANDING_PAGE_LIMITS' : 'BLOG_POST_LIMITS';
  limitsData[contentLimitKey] = {
    limit: contentUsageLimit,
    quantityUsed: contentQuantityUsed
  };

  if (contentType === ContentTypes.BLOG_POST) {
    limitsData.BLOG_LIMITS = {
      limit: blogUsageLimit,
      quantityUsed: blogQuantityUsed
    };
  }

  return limitsData;
});